import React, { useRef, useState } from "react";
import { FiArrowRight } from "react-icons/fi";
import { howToUseBySku } from "../../data/howToUse";
import { moqBySku } from "../../data/moq";
import { customizationBySku } from "../../data/customization";
import { getPackagingNote, packagingOptionsBySku } from "../../data/packaging";
import HowToUseIcon from "./HowToUseIcon";
import PackagingOptions, { resolvePackagingOption } from "./PackagingOptions";
import "./ProductDetailSections.scss";

const hasText = (value) => typeof value === "string" && value.trim().length > 0;
const textItems = (items) => Array.isArray(items) ? items.filter(hasText) : [];
const validId = (id) => hasText(id) && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(id);
const validImage = (image) => image && hasText(image.src) && typeof image.alt === "string"
  && !/placeholder/i.test(image.src);
const hasQuantity = (value) => (typeof value === "number" && Number.isFinite(value) && value > 0) || hasText(value);

function uniqueItems(items, isValid) {
  const seen = new Set();
  return (Array.isArray(items) ? items : []).filter((item) => {
    if (!item || !validId(item.id) || seen.has(item.id) || !isValid(item)) return false;
    seen.add(item.id);
    return true;
  });
}

// Validate only the supplied configuration; never infer options from a product family.
// Return new objects/arrays so editing or filtering one product cannot change another.
export function getRenderableDetailSections(product) {
  const packaging = packagingOptionsBySku[product.sku];
  const usage = howToUseBySku[product.sku];
  const moq = moqBySku[product.sku];
  const customization = customizationBySku[product.sku];
  const configured = Array.isArray(product.detailSections) ? product.detailSections : [];
  const packagingSection = packaging && {
    id: "packaging", type: "packaging", enabled: true, options: packaging,
    description: packaging.length === 1
      ? "Available packaging format for this product."
      : "Choose from multiple packaging formats based on your product, retail positioning and branding requirements.",
    note: getPackagingNote(product.sku),
  };
  let insertedPackaging = false;
  const withPackaging = configured.flatMap(section => {
    if (!packagingSection || section?.type !== "packaging") return [section];
    if (insertedPackaging) return [];
    insertedPackaging = true;
    return [{ ...packagingSection, id: section.id || "packaging" }];
  });
  if (packagingSection && !insertedPackaging) withPackaging.unshift(packagingSection);
  // Replace the old usage slot in place; all other optional modules keep their data and order.
  let insertedUsage = false;
  const withUsage = withPackaging.flatMap((section) => {
    if (!usage || section?.type !== "howToUse") return [section];
    if (insertedUsage) return [];
    insertedUsage = true;
    return [{ ...usage, id: section.id || "usage", type: "howToUse", enabled: true }];
  });
  if (usage && !insertedUsage) withUsage.push({ ...usage, id: "usage", type: "howToUse", enabled: true });
  let insertedCustomization = false;
  const withCustomization = withUsage.flatMap(section => {
    if (!customization || section?.type !== "customization") return [section];
    if (insertedCustomization) return [];
    insertedCustomization = true;
    return [{ ...customization, id: section.id || "customization" }];
  });
  if (customization && !insertedCustomization) {
    const moqIndex = withCustomization.findIndex(section => section?.type === "moq");
    withCustomization.splice(moqIndex < 0 ? withCustomization.length : moqIndex, 0, { ...customization });
  }
  // Central MOQ rows replace older rows with the same ID; retain other custom-order rows.
  let insertedMOQ = false;
  const withMOQ = withCustomization.flatMap(section => {
    if (!moq || section?.type !== "moq") return [section];
    if (insertedMOQ) return [];
    insertedMOQ = true;
    return [{
      ...section, ...moq, id: section.id || "moq", message: undefined,
      rows: [...moq.rows, ...(Array.isArray(section.rows)
        ? section.rows.filter(row => !moq.rows.some(updated => updated.id === row?.id)) : [])],
      note: section.note || moq.note, cta: section.cta || moq.cta,
    }];
  });
  if (moq && !insertedMOQ) withMOQ.push({ ...moq });
  const sections = withMOQ.map((section) => {
    if (!section || section.enabled === false) return null;
    switch (section.type) {
      case "packaging": {
        const options = uniqueItems((Array.isArray(section.options) ? section.options : []).map(resolvePackagingOption), () => true);
        return options.length ? { ...section, options } : null;
      }
      case "howToUse": {
        const steps = uniqueItems(section.steps, (step) => hasText(step.title) && hasText(step.description));
        return steps.length ? { ...section, steps } : null;
      }
      case "customization": {
        const categories = uniqueItems(section.categories, (category) => hasText(category.title)
          && (hasText(category.description) || textItems(category.items).length));
        return categories.length ? { ...section, categories } : null;
      }
      case "moq": {
        const rows = uniqueItems(section.rows, (row) => hasText(row.configuration) && hasQuantity(row.quantity) && hasText(row.unit));
        return (hasQuantity(section.quantity) && hasText(section.unit)) || hasText(section.message) || rows.length
          ? { ...section, rows } : null;
      }
      case "imageText":
        return hasText(section.title) && validImage(section.image)
          && (hasText(section.description) || textItems(section.highlights).length) ? { ...section } : null;
      default:
        return null;
    }
  });
  return uniqueItems(sections, () => true);
}

function DetailImage({ image }) {
  if (!validImage(image)) return null;
  return <img className="pds-image" src={image.src} alt={image.alt} width="1200" height="900"
    loading="lazy" decoding="async" data-fit={image.fit === "cover" ? "cover" : "contain"} />;
}

function SectionHeading({ section, headingId, defaultTitle }) {
  return <header className="pds-heading">
    {hasText(section.eyebrow) && <p className="products-eyebrow">{section.eyebrow}</p>}
    <h2 id={headingId}>{hasText(section.title) ? section.title : defaultTitle}</h2>
    {hasText(section.description) && <p>{section.description}</p>}
  </header>;
}

function Note({ children }) {
  return hasText(children) ? <p className="pds-note">{children}</p> : null;
}

function Highlights({ items }) {
  const content = textItems(items);
  return content.length ? <ul className="pds-highlights">{content.map((item, index) => <li key={`${index}-${item}`}>{item}</li>)}</ul> : null;
}

function InquiryCTA({ cta, onInquiry }) {
  return hasText(cta?.label) && typeof onInquiry === "function"
    ? <button type="button" className="products-btn products-btn-primary" onClick={onInquiry}>{cta.label}<FiArrowRight aria-hidden="true" /></button>
    : null;
}

function SectionContainer({ section, sectionId, className = "", children }) {
  return <section className={`pds-section ${className}`.trim()} id={sectionId}
    data-section-type={section.type} aria-labelledby={`${sectionId}--heading`}>{children}</section>;
}

export function PackagingOptionsSection({ section, sectionId }) {
  return <SectionContainer section={section} sectionId={sectionId} className="pds-packaging">
    <SectionHeading section={section} headingId={`${sectionId}--heading`} defaultTitle="Packaging Options" />
    <PackagingOptions options={section.options} />
    <Note>{section.note}</Note>
  </SectionContainer>;
}

export function HowToUseSection({ section, sectionId }) {
  return <SectionContainer section={section} sectionId={sectionId} className="pds-how-to-use">
    <SectionHeading section={section} headingId={`${sectionId}--heading`} defaultTitle="How to Use" />
    <div className="pds-usage-layout">
      <div className="pds-usage-copy">
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- Preserve list semantics in Safari when list-style is none. */}
        <ol className="pds-steps" role="list">
          {section.steps.map((step, index) => <li className="pds-step" key={step.id}>
            <HowToUseIcon iconKey={step.iconKey} />
            <div className="pds-step-copy">
              <span className="pds-step-number" aria-hidden="true">Step {index + 1}</span>
              <h3>{step.title}</h3><p>{step.description}</p>
            </div>
          </li>)}
        </ol>
        <Note>{section.note}</Note>
      </div>
      <DetailImage image={section.image} />
    </div>
  </SectionContainer>;
}

export function CustomizationOptionsSection({ section, sectionId, onInquiry }) {
  const [selectedId, setSelectedId] = useState(section.categories[0].id);
  const tabs = useRef([]);
  const activeId = section.categories.some((category) => category.id === selectedId) ? selectedId : section.categories[0].id;
  const hasTabs = section.categories.length > 1;
  function handleKeyDown(event, index) {
    const last = section.categories.length - 1;
    const next = { ArrowRight: (index + 1) % (last + 1), ArrowLeft: (index + last) % (last + 1), Home: 0, End: last }[event.key];
    if (next === undefined) return;
    event.preventDefault();
    setSelectedId(section.categories[next].id);
    tabs.current[next]?.focus();
  }
  return <SectionContainer section={section} sectionId={sectionId} className="pds-customization">
    <SectionHeading section={section} headingId={`${sectionId}--heading`} defaultTitle="Customize It for Your Brand" />
    {hasTabs && <div className="pds-tabs" role="tablist" aria-labelledby={`${sectionId}--heading`}>
      {section.categories.map((category, index) => <button type="button" role="tab" className="pds-tab" key={category.id}
        id={`${sectionId}--tab-${category.id}`} aria-controls={`${sectionId}--panel-${category.id}`}
        aria-selected={category.id === activeId} tabIndex={category.id === activeId ? 0 : -1}
        ref={(element) => { tabs.current[index] = element; }}
        onClick={() => setSelectedId(category.id)} onKeyDown={(event) => handleKeyDown(event, index)}>{category.tabLabel || category.title}</button>)}
    </div>}
    {section.categories.map((category) => <div key={category.id} className="pds-tab-panel"
      id={`${sectionId}--panel-${category.id}`} role={hasTabs ? "tabpanel" : undefined}
      aria-labelledby={hasTabs ? `${sectionId}--tab-${category.id}` : undefined}
      tabIndex={hasTabs ? 0 : undefined} hidden={category.id !== activeId}>
      <div className={`pds-split${validImage(category.image) ? "" : " pds-split--text-only"}`}>
        <div className="pds-copy"><h3>{category.title}</h3>
          {hasText(category.description) && <p>{category.description}</p>}
          <Highlights items={category.items} /><Note>{category.note}</Note>
        </div>
        <DetailImage image={category.image} />
      </div>
    </div>)}
    <Note>{section.note}</Note>
    <InquiryCTA cta={section.cta} onInquiry={onInquiry} />
  </SectionContainer>;
}

export function MOQSection({ section, sectionId, onInquiry }) {
  return <SectionContainer section={section} sectionId={sectionId} className="pds-moq">
    <div className="pds-moq-layout">
      <SectionHeading section={section} headingId={`${sectionId}--heading`} defaultTitle="Minimum Order Quantity" />
      {hasQuantity(section.quantity) && hasText(section.unit) ? <div className="pds-moq-value">
        <p className="pds-quantity">{section.quantity}</p><p className="pds-unit">{section.unit}</p>
        {hasText(section.scope) && <p className="pds-scope">{section.scope}</p>}
      </div> : hasText(section.message) ? <p className="pds-moq-message">{section.message}</p> : null}
    </div>
    {!!section.rows.length && <dl className="pds-moq-rows">{section.rows.map((row) => <div key={row.id} className="pds-moq-row">
      <dt>{row.configuration}</dt><dd>{row.quantity} {row.unit}</dd>{hasText(row.note) && <dd>{row.note}</dd>}
    </div>)}</dl>}
    <Note>{section.note}</Note><InquiryCTA cta={section.cta} onInquiry={onInquiry} />
  </SectionContainer>;
}

export function ImageTextSection({ section, sectionId, onInquiry }) {
  return <SectionContainer section={section} sectionId={sectionId}
    className={`pds-feature pds-feature--${section.imageSide === "right" ? "right" : "left"}${section.background === "soft" ? " pds-feature--soft" : ""}`}>
    <DetailImage image={section.image} />
    <div className="pds-copy">
      <SectionHeading section={section} headingId={`${sectionId}--heading`} />
      <Highlights items={section.highlights} /><InquiryCTA cta={section.cta} onInquiry={onInquiry} />
    </div>
  </SectionContainer>;
}

const sectionComponents = {
  packaging: PackagingOptionsSection,
  howToUse: HowToUseSection,
  customization: CustomizationOptionsSection,
  moq: MOQSection,
  imageText: ImageTextSection,
};

export default function ProductDetailSections({ product, onInquiry }) {
  const sections = getRenderableDetailSections(product);
  if (!sections.length) return null;
  return <div className="product-detail-sections" key={product.slug}>
    {sections.map((section) => {
      const Component = sectionComponents[section.type];
      return <Component key={section.id} section={section} sectionId={`product-${product.slug}-detail-${section.id}`} onInquiry={onInquiry} />;
    })}
  </div>;
}
